import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTodoContext } from '../../contexts/TodoContext';
import { TodoItem } from '../../types/todo';
import { isEligibleForToday } from '../../utils/today';
import { TODAY } from '../../utils/constants';
import LeftoverStep from './LeftoverStep';
import PickStep from './PickStep';
import OrderStep from './OrderStep';

type Step = 'leftovers' | 'pick' | 'order';

interface DailyReviewProps {
  onClose: () => void;
}

/**
 * DailyReview - the guided planning wizard: sweep leftovers, pick today's
 * tasks, put them in order.
 *
 * Everything is staged in local state and written back only on Finish, so
 * abandoning the wizard half-way leaves the existing plan exactly as it was.
 */
const DailyReview: React.FC<DailyReviewProps> = ({ onClose }) => {
  const { tree, today, isDayPlanStale, commitDayPlan } = useTodoContext();

  // Frozen at mount: the leftovers are yesterday's list, and the queue must not
  // shift underneath the user as they work through it.
  const [leftovers] = useState(() => {
    if (!isDayPlanStale) return { pending: [] as Array<{ id: string; item: TodoItem }>, completed: [] as Array<{ id: string; item: TodoItem }> };

    const pending: Array<{ id: string; item: TodoItem }> = [];
    const completed: Array<{ id: string; item: TodoItem }> = [];
    for (const id of today.items) {
      const item = tree[id];
      if (!item || item.archived) continue;
      if (item.done) {
        completed.push({ id, item });
      } else if (isEligibleForToday(tree, id)) {
        pending.push({ id, item });
      }
    }
    return { pending, completed };
  });

  // Re-planning a day that is already current starts from the existing list;
  // a new day starts empty and is built up from the leftover sweep onward.
  const [selected, setSelected] = useState<string[]>(() => (isDayPlanStale ? [] : [...today.items]));
  const [leftoverIndex, setLeftoverIndex] = useState(0);
  const [step, setStep] = useState<Step>(() => (leftoversPending() ? 'leftovers' : 'pick'));

  function leftoversPending(): boolean {
    return isDayPlanStale && today.items.some(id => tree[id] && !tree[id].archived && !tree[id].done);
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const advanceLeftover = useCallback((keep: boolean, id: string) => {
    if (keep) {
      setSelected(prev => (prev.includes(id) ? prev : [...prev, id]));
    }
    setLeftoverIndex(prev => {
      const next = prev + 1;
      if (next >= leftovers.pending.length) setStep('pick');
      return next;
    });
  }, [leftovers.pending.length]);

  const toggleSelected = useCallback((id: string) => {
    setSelected(prev => (prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]));
  }, []);

  const moveSelected = useCallback((from: number, to: number) => {
    setSelected(prev => {
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  const handleFinish = useCallback(() => {
    commitDayPlan(selected);
    onClose();
  }, [commitDayPlan, selected, onClose]);

  const currentLeftover = leftovers.pending[leftoverIndex];

  const footer = useMemo(() => {
    if (step === 'leftovers') return null;
    return (
      <div className="daily-review-footer">
        {step === 'order' && (
          <button className="daily-review-secondary" onClick={() => setStep('pick')}>{TODAY.BACK}</button>
        )}
        {step === 'pick' ? (
          <button className="daily-review-primary" onClick={() => setStep('order')}>{TODAY.NEXT}</button>
        ) : (
          <button className="daily-review-primary" onClick={handleFinish}>{TODAY.FINISH}</button>
        )}
      </div>
    );
  }, [step, handleFinish]);

  const content = (
    <div className="modal-backdrop" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="modal-container daily-review" role="dialog" aria-modal="true" aria-labelledby="daily-review-title">
        <div className="modal-header">
          <h3 id="daily-review-title" className="modal-title">{TODAY.REVIEW_TITLE}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close modal">×</button>
        </div>

        <div className="modal-body daily-review-body">
          {step === 'leftovers' && currentLeftover && (
            <LeftoverStep
              current={currentLeftover}
              index={leftoverIndex}
              total={leftovers.pending.length}
              completed={leftovers.completed}
              onKeep={() => advanceLeftover(true, currentLeftover.id)}
              onDefer={() => advanceLeftover(false, currentLeftover.id)}
              onDrop={() => advanceLeftover(false, currentLeftover.id)}
            />
          )}

          {step === 'pick' && (
            <PickStep tree={tree} selected={selected} onToggle={toggleSelected} />
          )}

          {step === 'order' && (
            <OrderStep tree={tree} selected={selected} onMove={moveSelected} />
          )}
        </div>

        {footer}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default DailyReview;
